import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Sparkles,
  LogOut,
  Target,
  BookOpen,
  CheckCircle2,
  CalendarDays,
  Heart,
  ShieldCheck,
  TrendingUp,
  Plus,
  UserPlus,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [supports, setSupports] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  // Load student list on mount
  useEffect(() => {
    api.get("/parent/students").then((r) => {
      setStudents(r.data);
      if (r.data.length > 0) setCurrentId(r.data[0].id);
      else setLoading(false); // no students at all
    });
  }, []);

  // Whenever currentId changes, load that student's data
  useEffect(() => {
    if (!currentId) return;
    setLoading(true);
    const q = `?student_id=${encodeURIComponent(currentId)}`;
    Promise.all([
      api.get(`/parent/summary${q}`),
      api.get(`/parent/goals${q}`),
      api.get(`/parent/lessons${q}`),
      api.get(`/parent/supports${q}`),
      api.get(`/parent/meetings${q}`),
    ])
      .then(([s, g, l, sup, m]) => {
        setSummary(s.data);
        setGoals(g.data);
        setLessons(l.data);
        setSupports(sup.data);
        setMeetings(m.data);
      })
      .finally(() => setLoading(false));
  }, [currentId]);

  const refreshStudents = async () => {
    const r = await api.get("/parent/students");
    setStudents(r.data);
    return r.data;
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B726F]">
        <span className="calm-pulse">Loading…</span>
      </div>
    );
  }

  const s = summary?.student;
  const supportsByCat = {
    accommodation: supports.filter((x) => x.category === "accommodation"),
    modification: supports.filter((x) => x.category === "modification"),
    related_service: supports.filter((x) => x.category === "related_service"),
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9]">
      <header className="border-b border-[#E8E9E6] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#6E5AA0] flex items-center justify-center">
              <Sparkles className="text-white" size={16} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold ink leading-none">Ellis Path</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B726F] mt-1">Family view</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium ink" data-testid="parent-name">{user?.name}</div>
              <div className="text-xs ink-muted">{user?.email}</div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { logout(); navigate("/login"); }}
              className="text-[#6B726F] hover:text-[#BA6356]"
              data-testid="parent-logout-button"
            >
              <LogOut size={16} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-8" data-testid="parent-dashboard">
        {/* Student switcher */}
        {students.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="student-switcher">
            {students.map((st) => (
              <button
                key={st.id}
                onClick={() => setCurrentId(st.id)}
                data-testid={`switch-student-${st.id}`}
                className={`px-4 py-2 rounded-full border text-sm transition-all ${st.id === currentId ? "bg-[#6E5AA0] text-white border-[#6E5AA0]" : "bg-white text-[#2D322F] border-[#E8E9E6] hover:bg-[#F0EBF6]"}`}
              >
                {st.first_name} {st.last_name}
                {!st.is_assigned && <span className={`ml-2 text-[10px] uppercase tracking-wider ${st.id === currentId ? "text-white/80" : "text-[#7C5B25]"}`}>Pending</span>}
              </button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="rounded-full border-dashed"
              data-testid="parent-add-student-button"
            >
              <Plus size={14} className="mr-2" /> Add another student
            </Button>
          </div>
        )}

        {students.length === 0 && (
          <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
            <UserPlus className="mx-auto text-[#6E5AA0]" size={26} />
            <div className="font-heading text-xl font-medium ink mt-3">Add your first student</div>
            <p className="ink-muted text-sm mt-2 max-w-md mx-auto">
              Enter your child's information and we'll notify the school team to assign a teacher.
            </p>
            <Button onClick={() => setAddOpen(true)} className="mt-5 bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="parent-add-student-empty">
              <Plus size={14} className="mr-2" /> Add a student
            </Button>
          </Card>
        )}

        {s && (
          <>
        <section>
          <div className="overline">Welcome</div>
          <h1 className="font-heading text-4xl font-medium ink mt-3">
            How {s.first_name} is doing
          </h1>
          <p className="ink-muted mt-2">
            {summary.is_assigned ? (
              <>
                A friendly snapshot of {s.first_name}'s goals, lessons, supports, and upcoming meetings. Shared with you by{" "}
                <span className="ink font-medium">{summary.teacher?.name || "the teaching team"}</span>.
              </>
            ) : (
              <>{s.first_name} is <span className="ink font-medium">awaiting teacher assignment</span>. Once the school team assigns a teacher, live updates will appear here.</>
            )}
          </p>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Badge className="bg-[#EFEAF5] text-[#574785] hover:bg-[#EFEAF5]">Grade {s.grade}</Badge>
            {s.disability_category && <Badge variant="outline">{s.disability_category}</Badge>}
            {!summary.is_assigned && <Badge className="bg-[#F3E6D4] text-[#7C5B25] hover:bg-[#F3E6D4]">Pending assignment</Badge>}
          </div>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <Stat icon={Target} label="Active goals" value={summary.counts.goals} testid="stat-goals" />
          <Stat icon={CheckCircle2} label="Lessons completed" value={summary.counts.lessons_completed} testid="stat-completed" />
          <Stat icon={BookOpen} label="Lessons assigned" value={summary.counts.lessons} testid="stat-lessons" />
          <Stat icon={ShieldCheck} label="Supports in place" value={summary.counts.supports} testid="stat-supports" />
        </section>

        <Tabs defaultValue="goals" className="">
          <TabsList className="bg-white border border-[#E8E9E6] rounded-xl p-1 h-auto flex-wrap">
            <TabsTrigger value="goals" data-testid="parent-tab-goals" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Goals & Progress</TabsTrigger>
            <TabsTrigger value="lessons" data-testid="parent-tab-lessons" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Lessons</TabsTrigger>
            <TabsTrigger value="supports" data-testid="parent-tab-supports" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Supports</TabsTrigger>
            <TabsTrigger value="meetings" data-testid="parent-tab-meetings" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Meetings</TabsTrigger>
          </TabsList>

          <TabsContent value="goals" className="mt-6">
            <GoalsView goals={goals} />
          </TabsContent>

          <TabsContent value="lessons" className="mt-6">
            <LessonsView lessons={lessons} />
          </TabsContent>

          <TabsContent value="supports" className="mt-6">
            <SupportsView grouped={supportsByCat} />
          </TabsContent>

          <TabsContent value="meetings" className="mt-6">
            <MeetingsView meetings={meetings} />
          </TabsContent>
        </Tabs>

        <Card className="p-6 border border-[#E8E9E6] bg-white flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#F0EBF6] flex items-center justify-center shrink-0">
            <Heart className="text-[#6E5AA0]" size={18} />
          </div>
          <div>
            <div className="font-medium ink">A note on privacy</div>
            <p className="ink-muted text-sm mt-1">
              You're seeing the parts of {s.first_name}'s plan shared with families: goals, progress, lessons,
              supports, and meeting dates. Detailed clinical notes and the full IEP document remain with the
              school team. Reach out to {summary.teacher?.name || "the teacher"} any time you have questions.
            </p>
          </div>
        </Card>
          </>
        )}
      </main>

      {addOpen && (
        <AddStudentDialog
          onClose={() => setAddOpen(false)}
          onCreated={async (newStudentId) => {
            setAddOpen(false);
            await refreshStudents();
            setCurrentId(newStudentId);
          }}
        />
      )}
    </div>
  );
}

function AddStudentDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    grade: "",
    school: "",
    home_address: "",
  });
  const [files, setFiles] = useState([]); // {file, doc_type}
  const [saving, setSaving] = useState(false);

  const DOC_TYPES = [
    { value: "iep", label: "IEP" },
    { value: "progress_report", label: "Progress report" },
    { value: "report_card", label: "Report card" },
    { value: "evaluation", label: "Evaluation" },
    { value: "other", label: "Other" },
  ];

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.grade) {
      toast.error("First name, last name, and grade are required.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/parent/students", form);
      const newId = r.data.id;
      // upload each file
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        try {
          await api.post(
            `/documents/upload?doc_type=${encodeURIComponent(f.doc_type)}&student_id=${encodeURIComponent(newId)}`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        } catch {
          toast.error(`Could not upload ${f.file.name}`);
        }
      }
      toast.success(`${form.first_name} added. The school team will assign a teacher soon.`);
      onCreated(newId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add student");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add a student</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>First name</Label>
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="add-student-first-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Last name</Label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="add-student-last-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Date of birth</Label>
            <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="add-student-dob" />
          </div>
          <div className="space-y-1.5">
            <Label>Grade</Label>
            <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g., 3" data-testid="add-student-grade" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>School</Label>
            <Input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="add-student-school" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Home address</Label>
            <Textarea rows={2} value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} data-testid="add-student-address" />
          </div>
        </div>

        <div className="pt-2 border-t border-[#E8E9E6]">
          <Label>Supporting documents (optional)</Label>
          <p className="text-xs ink-muted mt-1">IEPs, progress reports, report cards, evaluations, etc.</p>
          <div className="mt-2 space-y-2">
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.webp,.txt"
              onChange={(e) => setFiles([...files, ...Array.from(e.target.files || []).map((f) => ({ file: f, doc_type: "other" }))])}
              className="text-sm"
              data-testid="add-student-files-input"
            />
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 border border-[#EDEDE9] rounded-lg p-2 bg-[#FBFBF9] text-sm">
                <span className="flex-1 truncate">{f.file.name}</span>
                <select
                  value={f.doc_type}
                  onChange={(e) => setFiles(files.map((x, j) => j === i ? { ...x, doc_type: e.target.value } : x))}
                  className="border rounded px-2 py-1 text-xs bg-white"
                >
                  {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <button className="text-[#BA6356] text-xs" onClick={() => setFiles(files.filter((_, j) => j !== i))}>remove</button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="add-student-save">
            {saving ? "Saving…" : "Add student"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-5 border border-[#E8E9E6] bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{label}</div>
          <div className="font-heading text-3xl font-medium ink mt-2">{value ?? 0}</div>
        </div>
        <div className="w-9 h-9 rounded-lg bg-[#F0EBF6] flex items-center justify-center">
          <Icon size={16} className="text-[#6E5AA0]" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

function GoalsView({ goals }) {
  const [selected, setSelected] = useState(goals[0]?.id || "");
  const [progress, setProgress] = useState([]);

  useEffect(() => {
    if (!selected && goals[0]) setSelected(goals[0].id);
  }, [goals, selected]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/parent/goals/${selected}/progress`).then((r) => setProgress(r.data));
  }, [selected]);

  if (goals.length === 0) {
    return (
      <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
        <p className="ink-muted">No goals shared yet. They'll appear here once your teacher saves the IEP.</p>
      </Card>
    );
  }

  const chart = progress.map((p) => ({ date: new Date(p.date).toLocaleDateString(), score: p.score }));

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-3">
        {goals.map((g) => (
          <button
            key={g.id}
            onClick={() => setSelected(g.id)}
            data-testid={`parent-goal-${g.id}`}
            className={`text-left w-full border rounded-xl p-4 transition-all ${selected === g.id ? "border-[#6E5AA0] bg-[#EFEAF5]" : "border-[#E8E9E6] bg-white hover:bg-[#F0EBF6]"}`}
          >
            <Badge variant="outline" className="bg-white">{g.area}</Badge>
            <p className="ink mt-2 text-sm">{g.description}</p>
          </button>
        ))}
      </div>
      <Card className="lg:col-span-3 p-6 border border-[#E8E9E6] bg-white">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={16} className="text-[#6E5AA0]" />
          <div className="font-heading text-lg font-medium ink">Progress over time</div>
        </div>
        {chart.length === 0 ? (
          <div className="ink-muted text-sm py-16 text-center">No data points yet for this goal.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
                <CartesianGrid stroke="#E8E9E6" vertical={false} />
                <XAxis dataKey="date" stroke="#6B726F" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} stroke="#6B726F" tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#6E5AA0" strokeWidth={2.5} dot={{ r: 4, fill: "#6E5AA0" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}

function LessonsView({ lessons }) {
  if (lessons.length === 0) {
    return (
      <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
        <p className="ink-muted">No lessons shared yet.</p>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {lessons.map((l) => {
        const done = l.latest_completion?.status === "completed";
        return (
          <Card key={l.id} className="p-5 border border-[#E8E9E6] bg-white" data-testid={`parent-lesson-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg font-medium ink">{l.title}</div>
                {l.standard && <div className="text-xs ink-muted mt-1">{l.standard}</div>}
                {l.objective && <p className="text-sm ink mt-2 line-clamp-3">{l.objective}</p>}
              </div>
              {done ? (
                <Badge className="bg-[#E1E9DE] text-[#3C5C40] hover:bg-[#E1E9DE]"><CheckCircle2 size={12} className="mr-1" />Completed</Badge>
              ) : (
                <Badge variant="outline" className="bg-white">Assigned</Badge>
              )}
            </div>
            {l.latest_completion?.score != null && (
              <div className="text-xs ink-muted mt-3">Score: <span className="ink font-medium">{l.latest_completion.score}%</span></div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function SupportsView({ grouped }) {
  const labels = { accommodation: "Accommodations", modification: "Modifications", related_service: "Related services" };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {Object.entries(grouped).map(([cat, items]) => (
        <Card key={cat} className="p-6 border border-[#E8E9E6] bg-white" data-testid={`parent-support-cat-${cat}`}>
          <div className="overline">{labels[cat]}</div>
          <div className="font-heading text-3xl font-medium ink mt-1">{items.length}</div>
          <div className="mt-4 space-y-2">
            {items.length === 0 && <div className="text-sm ink-muted">None on file.</div>}
            {items.map((s) => (
              <div key={s.id} className="border border-[#EDEDE9] rounded-lg px-3 py-2 text-sm bg-[#FBFBF9]">
                <div className="ink">{s.title}</div>
                {s.frequency && <div className="text-xs ink-muted">{s.frequency}</div>}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function MeetingsView({ meetings }) {
  const now = new Date();
  const upcoming = meetings.filter((m) => new Date(m.scheduled_at) >= now);
  const past = meetings.filter((m) => new Date(m.scheduled_at) < now);

  const Row = ({ m }) => (
    <Card key={m.id} className="p-5 border border-[#E8E9E6] bg-white" data-testid={`parent-meeting-${m.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-[#F0EBF6] flex items-center justify-center">
          <CalendarDays size={16} className="text-[#6E5AA0]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium ink">{m.title}</div>
          <div className="text-xs ink-muted mt-1">
            {new Date(m.scheduled_at).toLocaleString()} · {m.duration_minutes} min · {m.location || "Location TBD"}
          </div>
          {m.attendees?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {m.attendees.map((a, i) => <Badge key={i} variant="outline" className="bg-[#EFEAF5]">{a}</Badge>)}
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="font-heading text-lg font-medium ink mb-3">Upcoming</div>
        {upcoming.length === 0 ? (
          <Card className="p-6 border-dashed border-[#D8D9D5] bg-white text-center">
            <p className="ink-muted text-sm">No upcoming meetings yet.</p>
          </Card>
        ) : <div className="space-y-3">{upcoming.map((m) => <Row key={m.id} m={m} />)}</div>}
      </div>
      <div>
        <div className="font-heading text-lg font-medium ink mb-3">Past</div>
        {past.length === 0 ? (
          <Card className="p-6 border-dashed border-[#D8D9D5] bg-white text-center">
            <p className="ink-muted text-sm">No past meetings yet.</p>
          </Card>
        ) : <div className="space-y-3">{past.map((m) => <Row key={m.id} m={m} />)}</div>}
      </div>
    </div>
  );
}
